import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Search, Filter, UserPlus, CreditCard, CheckCircle2,
  XCircle, Banknote, RefreshCw, DollarSign, Receipt, FileCheck,
  ChevronRight, User, Calendar, Hash, Info,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  activities, activityTypeConfig, formatRelativeTime,
  type Activity, type ActivityType,
} from "@/data/activityData";
import { formatCurrency } from "@/data/loanData";

const typeIcons: Record<ActivityType, React.ElementType> = {
  loan_applied:      CreditCard,
  loan_approved:     CheckCircle2,
  loan_rejected:     XCircle,
  loan_disbursed:    Banknote,
  loan_repaid:       RefreshCw,
  customer_added:    UserPlus,
  lead_created:      User,
  payment_received:  DollarSign,
  expense_submitted: Receipt,
  expense_approved:  FileCheck,
};

const statusForType: Record<ActivityType, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  loan_applied:      { label: "Pending Review", variant: "secondary" },
  loan_approved:     { label: "Approved",       variant: "default" },
  loan_rejected:     { label: "Rejected",       variant: "destructive" },
  loan_disbursed:    { label: "Disbursed",      variant: "default" },
  loan_repaid:       { label: "Completed",      variant: "default" },
  customer_added:    { label: "Active",         variant: "default" },
  lead_created:      { label: "New",            variant: "secondary" },
  payment_received:  { label: "Received",       variant: "default" },
  expense_submitted: { label: "Pending",        variant: "secondary" },
  expense_approved:  { label: "Approved",       variant: "default" },
};

const typeFilterOptions: { value: string; label: string }[] = [
  { value: "all",              label: "All Activities" },
  { value: "loan_applied",     label: "Loan Applications" },
  { value: "loan_approved",    label: "Loan Approvals" },
  { value: "loan_rejected",    label: "Loan Rejections" },
  { value: "loan_disbursed",   label: "Disbursements" },
  { value: "loan_repaid",      label: "Loan Repayments" },
  { value: "customer_added",   label: "Customers Added" },
  { value: "lead_created",     label: "Leads Created" },
  { value: "payment_received", label: "Payments Received" },
  { value: "expense_submitted","label": "Expenses Submitted" },
  { value: "expense_approved", "label": "Expenses Approved" },
];

function ActivityIcon({ type }: { type: ActivityType }) {
  const Icon = typeIcons[type];
  const cfg = activityTypeConfig[type];
  return (
    <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${cfg.bgColor}`}>
      <Icon className={`h-4 w-4 ${cfg.color}`} />
    </div>
  );
}

export default function ActivityPage() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState<Activity | null>(null);

  const filtered = useMemo(() =>
    activities.filter((a) => {
      const matchSearch =
        a.title.toLowerCase().includes(search.toLowerCase()) ||
        a.entityName.toLowerCase().includes(search.toLowerCase()) ||
        a.description.toLowerCase().includes(search.toLowerCase());
      const matchType = typeFilter === "all" || a.type === typeFilter;
      return matchSearch && matchType;
    }),
    [search, typeFilter]
  );

  // Stats
  const stats = useMemo(() => ({
    total:      activities.length,
    loans:      activities.filter((a) => a.type.startsWith("loan")).length,
    payments:   activities.filter((a) => a.type === "payment_received" || a.type === "loan_repaid").length,
    pending:    activities.filter((a) => ["loan_applied", "expense_submitted"].includes(a.type)).length,
  }), []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">Activity Log</h1>
              <p className="text-xs text-muted-foreground">Track all system events in real-time</p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            {filtered.length} events
          </Badge>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Events",     value: stats.total,    icon: Info,        color: "text-muted-foreground" },
            { label: "Loan Activities",  value: stats.loans,    icon: CreditCard,  color: "text-primary" },
            { label: "Payments",         value: stats.payments, icon: DollarSign,  color: "text-chart-1" },
            { label: "Pending Actions",  value: stats.pending,  icon: Filter,      color: "text-chart-4" },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <s.icon className={`h-4 w-4 ${s.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" /> Filter Activities
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, entity, or description..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {typeFilterOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Activity Feed */}
        <Card>
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <Search className="h-8 w-8 mb-3 opacity-40" />
                <p className="text-sm">No activities match your filters</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filtered.map((activity) => {
                  const cfg = activityTypeConfig[activity.type];
                  const status = statusForType[activity.type];
                  return (
                    <li key={activity.id}>
                      <button
                        onClick={() => setSelected(activity)}
                        className="w-full text-left flex items-center gap-4 px-5 py-4 hover:bg-muted/40 transition-colors group"
                      >
                        <ActivityIcon type={activity.type} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-foreground truncate">{activity.title}</span>
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${cfg.bgColor} ${cfg.color}`}>
                              {cfg.label}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{activity.description}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-muted-foreground">{activity.entityName}</span>
                            <span className="text-xs text-muted-foreground">·</span>
                            <span className="text-xs text-muted-foreground">{formatRelativeTime(activity.timestamp)}</span>
                            {activity.amount && (
                              <>
                                <span className="text-xs text-muted-foreground">·</span>
                                <span className="text-xs font-medium text-foreground">KES {formatCurrency(activity.amount)}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant={status.variant} className="hidden sm:inline-flex text-xs">
                            {status.label}
                          </Badge>
                          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Detail Sheet */}
      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {selected && (() => {
            const cfg = activityTypeConfig[selected.type];
            const Icon = typeIcons[selected.type];
            const status = statusForType[selected.type];
            return (
              <>
                <SheetHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${cfg.bgColor}`}>
                      <Icon className={`h-5 w-5 ${cfg.color}`} />
                    </div>
                    <div>
                      <SheetTitle className="text-base">{selected.title}</SheetTitle>
                      <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                    </div>
                  </div>
                </SheetHeader>

                <Separator />

                <div className="py-4 space-y-4">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge variant={status.variant}>{status.label}</Badge>
                  </div>

                  {/* Description */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Description</p>
                    <p className="text-sm text-foreground">{selected.description}</p>
                  </div>

                  <Separator />

                  {/* Key Details */}
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Details</p>

                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm text-muted-foreground">Reference</span>
                      <span className="ml-auto text-sm font-mono font-medium text-foreground">{selected.entityId}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm text-muted-foreground">Entity</span>
                      <span className="ml-auto text-sm font-medium text-foreground">{selected.entityName}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm text-muted-foreground">Performed by</span>
                      <span className="ml-auto text-sm font-medium text-foreground">{selected.actor}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm text-muted-foreground">Time</span>
                      <span className="ml-auto text-sm text-foreground">
                        {new Date(selected.timestamp).toLocaleString("en-GB", {
                          day: "2-digit", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </span>
                    </div>

                    {selected.amount && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm text-muted-foreground">Amount</span>
                        <span className="ml-auto text-sm font-bold text-foreground">KES {formatCurrency(selected.amount)}</span>
                      </div>
                    )}
                  </div>

                  {/* Meta */}
                  {selected.meta && Object.keys(selected.meta).length > 0 && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Additional Info</p>
                        {Object.entries(selected.meta).map(([key, val]) => (
                          <div key={key} className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, " ")}</span>
                            <span className="text-sm font-medium text-foreground">{val}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Action buttons for pending loans */}
                  {selected.type === "loan_applied" && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</p>
                        <div className="flex gap-2">
                          <Button className="flex-1" size="sm">
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button variant="destructive" className="flex-1" size="sm">
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {selected.type === "loan_approved" && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</p>
                        <Button className="w-full" size="sm">
                          <Banknote className="h-4 w-4 mr-1" /> Disburse Funds
                        </Button>
                      </div>
                    </>
                  )}

                  {selected.type === "expense_submitted" && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</p>
                        <div className="flex gap-2">
                          <Button className="flex-1" size="sm">
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button variant="destructive" className="flex-1" size="sm">
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}
